import React, { useCallback } from 'react';
import { Image as ImageIcon, Loader2, Sparkles, UploadCloud, X } from 'lucide-react';
import MediaSourceButtons, { ImageDropZone, useGalleryMediaIngest } from './MediaSourceButtons';

export type ImageRefRole = 'start' | 'end' | null;

export type ReferenceImageItem = {
  id: string;
  image_b64: string;
  role: ImageRefRole;
  label?: string;
};

type Props = {
  images: ReferenceImageItem[];
  max: number;
  accent?: string;
  emptyHint?: string;
  allowedRoles?: Array<'start' | 'end'>;
  modelLabel?: string;
  onAdd: (dataUrl: string, meta?: { title?: string }) => void | Promise<void>;
  onRemove: (id: string) => void;
  onSetRole: (id: string, role: ImageRefRole) => void;
  onTransform?: (id: string) => void;
  compact?: boolean;
};

export default function ReferenceImageTray({
  images,
  max,
  accent = '#6366f1',
  emptyHint = 'Envie fotos da galeria ou upload. Marcar início e fim é opcional.',
  allowedRoles = ['start', 'end'],
  modelLabel,
  onAdd,
  onRemove,
  onSetRole,
  onTransform,
  compact = false,
}: Props) {
  const canAdd = images.length < max;
  const showStart = allowedRoles.includes('start');
  const showEnd = allowedRoles.includes('end');
  const thumbH = compact ? 96 : 148;
  const fillPct = max > 0 ? Math.min(100, (images.length / max) * 100) : 0;

  const handleSelected = useCallback(
    (dataUrl: string, meta?: { fromGallery?: boolean; title?: string }) => onAdd(dataUrl, meta),
    [onAdd],
  );
  const { ingestFiles, uploading } = useGalleryMediaIngest({ onImageSelected: handleSelected });

  const sourceButtons = (opts?: { dense?: boolean; stacked?: boolean; compact?: boolean }) => (
    <MediaSourceButtons
      accept="image"
      accent={accent}
      compact={opts?.compact ?? compact}
      dense={opts?.dense}
      stacked={opts?.stacked}
      multiple
      disabled={!canAdd || uploading}
      ingestFiles={ingestFiles}
      uploading={uploading}
      onImageSelected={handleSelected}
    />
  );

  return (
    <ImageDropZone
      className="ref-tray"
      disabled={!canAdd || uploading}
      onFiles={ingestFiles}
      hint="Solte as imagens para adicionar"
      style={{ padding: compact ? '0.75rem' : '1.15rem 1.2rem 1.05rem' }}
    >
      <style>{trayCss}</style>
      {uploading && (
        <div className="ref-uploading">
          <Loader2 size={18} className="spin" />
          Carregando imagens…
        </div>
      )}
      {images.length === 0 ? (
        <div className="ref-tray-empty" style={{ minHeight: compact ? 150 : 228 }}>
          <UploadCloud size={compact ? 28 : 38} style={{ opacity: 0.4 }} />
          <span style={{ fontSize: compact ? '0.78rem' : '0.9rem', textAlign: 'center', lineHeight: 1.45, maxWidth: 420 }}>
            {emptyHint}
          </span>
          <span style={{ fontSize: '0.72rem', opacity: 0.75 }}>Arraste várias fotos, escolha na galeria ou faça upload</span>
          {sourceButtons()}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: compact ? 10 : 14 }}>
          <div
            className="ref-tray-grid"
            style={{
              gridTemplateColumns: `repeat(auto-fill, minmax(${compact ? 128 : 168}px, 1fr))`,
              maxHeight: images.length > 8 ? (compact ? 280 : 440) : undefined,
              overflowY: images.length > 8 ? 'auto' : undefined,
              paddingRight: images.length > 8 ? 4 : 0,
            }}
          >
            {images.map((img, idx) => {
              const isStart = img.role === 'start';
              const isEnd = img.role === 'end';
              const ring = isStart ? '#22c55e' : isEnd ? '#c084fc' : 'rgba(255,255,255,0.08)';
              return (
                <article
                  key={img.id}
                  className="ref-card"
                  style={{
                    borderColor: ring,
                    boxShadow: isStart
                      ? '0 0 0 1px rgba(34,197,94,0.35), 0 10px 24px rgba(0,0,0,0.28)'
                      : isEnd
                        ? '0 0 0 1px rgba(192,132,252,0.4), 0 10px 24px rgba(0,0,0,0.28)'
                        : '0 8px 20px rgba(0,0,0,0.22)',
                  }}
                >
                  <div className="ref-card-media" style={{ height: thumbH }}>
                    <img src={img.image_b64} alt={img.label || `Referência ${idx + 1}`} />
                    <span className="ref-idx">{idx + 1}</span>
                    {(isStart || isEnd) && (
                      <span className={`ref-chip ${isStart ? 'is-start' : 'is-end'}`}>
                        {isStart ? 'Início' : 'Fim'}
                      </span>
                    )}
                    <button
                      type="button"
                      className="ref-x"
                      onClick={() => onRemove(img.id)}
                      aria-label="Remover imagem"
                    >
                      <X size={12} />
                    </button>
                    {onTransform && (
                      <button
                        type="button"
                        className="ref-ai"
                        onClick={() => onTransform(img.id)}
                        title="Transformar com IA"
                        aria-label="Transformar com IA"
                        style={{ background: accent }}
                      >
                        <Sparkles size={12} />
                      </button>
                    )}
                  </div>
                  {(showStart || showEnd) && (
                    <div
                      className="ref-roles"
                      role="group"
                      aria-label="Papel na timeline"
                      style={{ gridTemplateColumns: showStart && showEnd ? '1fr 1fr' : '1fr' }}
                    >
                      {showStart && (
                        <button
                          type="button"
                          className={isStart ? 'on-start' : ''}
                          onClick={() => onSetRole(img.id, isStart ? null : 'start')}
                        >
                          Início
                        </button>
                      )}
                      {showEnd && (
                        <button
                          type="button"
                          className={isEnd ? 'on-end' : ''}
                          onClick={() => onSetRole(img.id, isEnd ? null : 'end')}
                        >
                          Fim
                        </button>
                      )}
                    </div>
                  )}
                </article>
              );
            })}
            {canAdd && (
              <div className="ref-add" style={{ minHeight: thumbH + ((showStart || showEnd) ? 40 : 0) }}>
                <ImageIcon size={18} style={{ opacity: 0.45 }} />
                <span>Adicionar</span>
                {sourceButtons({ compact: true, dense: true, stacked: true })}
              </div>
            )}
          </div>

          <div className="ref-meta">
            <div className="ref-meter" aria-hidden>
              <div style={{ width: `${fillPct}%`, background: accent }} />
            </div>
            <div className="ref-meta-row">
              <strong>
                {images.length}
                <span> / {max}</span>
              </strong>
              <span>
                {modelLabel
                  ? `${modelLabel} aceita até ${max} referências`
                  : `Limite deste modelo: ${max} imagens`}
              </span>
            </div>
            {(showStart || showEnd) && (
              <p>
                Toque em <em>Início</em> ou <em>Fim</em> só se quiser fixar abertura e encerramento.
                Sem marca, a IA usa o conjunto inteiro no roteiro.
              </p>
            )}
          </div>
        </div>
      )}
    </ImageDropZone>
  );
}

const trayCss = `
.ref-tray-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 1.75rem 1.2rem;
  color: var(--text-muted);
  border-radius: 16px;
  border: 1.5px dashed rgba(255,255,255,0.14);
  background: radial-gradient(circle at top, rgba(99,102,241,0.08), transparent 55%);
}
.ref-tray-grid {
  display: grid;
  gap: 0.75rem;
}
.ref-card {
  border-radius: 14px;
  overflow: hidden;
  border: 1.5px solid rgba(255,255,255,0.08);
  background: #12141a;
  display: flex;
  flex-direction: column;
}
.ref-card-media {
  position: relative;
  background:
    linear-gradient(45deg, rgba(255,255,255,0.03) 25%, transparent 25%),
    linear-gradient(-45deg, rgba(255,255,255,0.03) 25%, transparent 25%),
    #0c0d12;
  background-size: 16px 16px;
  background-position: 0 0, 0 8px;
}
.ref-card-media img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
}
.ref-idx {
  position: absolute;
  top: 8px;
  left: 8px;
  min-width: 22px;
  height: 22px;
  padding: 0 6px;
  border-radius: 999px;
  background: rgba(0,0,0,0.72);
  color: #fff;
  font-size: 0.68rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(6px);
}
.ref-chip {
  position: absolute;
  top: 8px;
  left: 36px;
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  padding: 3px 8px;
  border-radius: 999px;
  color: #fff;
}
.ref-chip.is-start { background: rgba(34,197,94,0.95); }
.ref-chip.is-end { background: rgba(168,85,247,0.95); }
.ref-x, .ref-ai {
  position: absolute;
  border: none;
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}
.ref-x {
  top: 8px;
  right: 8px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: rgba(0,0,0,0.7);
}
.ref-x:hover { background: #ef4444; }
.ref-ai {
  bottom: 8px;
  right: 8px;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.35);
}
.ref-ai:hover { filter: brightness(1.12); }
.ref-roles {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1px;
  background: rgba(255,255,255,0.06);
}
.ref-roles button {
  border: none;
  background: rgba(0,0,0,0.35);
  color: rgba(255,255,255,0.55);
  font-size: 0.7rem;
  font-weight: 650;
  font-family: inherit;
  padding: 8px 4px;
  cursor: pointer;
}
.ref-roles button:hover { color: #fff; background: rgba(255,255,255,0.06); }
.ref-roles button.on-start { background: #16a34a; color: #fff; }
.ref-roles button.on-end { background: #9333ea; color: #fff; }
.ref-add {
  border-radius: 14px;
  border: 1.5px dashed rgba(255,255,255,0.16);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 10px;
  color: var(--text-muted);
  background: rgba(255,255,255,0.02);
}
.ref-add span { font-size: 0.72rem; font-weight: 600; }
.ref-meta { display: flex; flex-direction: column; gap: 6px; }
.ref-meter {
  height: 4px;
  border-radius: 99px;
  background: rgba(255,255,255,0.08);
  overflow: hidden;
}
.ref-meter > div { height: 100%; border-radius: 99px; transition: width 0.25s ease; }
.ref-meta-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  font-size: 0.72rem;
  color: var(--text-muted);
}
.ref-meta-row strong { color: #fff; font-size: 0.86rem; }
.ref-meta-row strong span { color: var(--text-muted); font-weight: 500; }
.ref-meta p {
  margin: 0;
  font-size: 0.7rem;
  line-height: 1.45;
  color: var(--text-muted);
}
.ref-meta em { color: rgba(255,255,255,0.78); font-style: normal; font-weight: 600; }
.ref-uploading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-bottom: 10px;
  font-size: 0.78rem;
  color: #c7d2fe;
}
.spin { animation: spin 0.8s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }
`;
